import z from 'zod';

import { BlueskyCodec, PlateBluesky, UnityBluesky } from './Bluesky';
import {
    CrossheadCodec,
    PlateCrossheadDefault,
    PlateCrossheadOrderedListicle,
    PlateCrossheadSubsection,
    PlateCrossheadUnorderedListicle,
    UnityCrosshead,
} from './Crosshead';
import {
    EmbeddedContentCodec,
    PlateEmbeddedContent,
    UnityEmbeddedContent,
} from './EmbeddedContent';
import { FacebookCodec, PlateFacebook, UnityFacebook } from './Facebook';
import { FooterCodec, PlateFooter, UnityFooter } from './Footer';
import {
    FrontendComponentCodec,
    PlateFrontendComponent,
    UnityFrontendComponent,
} from './FrontendComponent';
import { ImageCodec, PlateImage, UnityImage } from './Image';
import { InfoboxCodec, PlateInfobox, UnityInfobox } from './Infobox';
import { InstagramCodec, PlateInstagram, UnityInstagram } from './Instagram';
import {
    InterviewSegmentCodec,
    PlateInterviewSegment,
    UnityInterviewSegment,
} from './InterviewSegment';
import {
    OrderedListCodec,
    PlateOrderedList,
    PlateUnorderedList,
    UnityOrderedList,
    UnityUnorderedList,
    UnorderedListCodec,
} from './List';
import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import { PlatePoll, PollCodec, UnityPoll } from './Poll';
import { PlateQuote, QuoteCodec, UnityQuote } from './Quote';
import { PlateRatingPoll, RatingPollCodec, UnityRatingPoll } from './RatingPoll';
import { PlateSeparator, SeparatorCodec, UnitySeparator } from './Separator';
import { PlateSlideshow, SlideshowCodec, UnitySlideshow } from './Slideshow';
import { PlateThreads, ThreadsCodec, UnityThreads } from './Threads';
import { PlateTwitter, TwitterCodec, UnityTwitter } from './Twitter';
import { PlateVideoCms, UnityVideoCms, VideoCmsCodec } from './VideoCms';
import { PlateYoutube, UnityYoutube, YoutubeCodec } from './Youtube';
import {
    CURRENT_VERSION,
    ElementId,
    EmptyChildren,
    emptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

const PlateSnippetElement = z.discriminatedUnion(
    'type',
    [
        PlateCrossheadDefault,
        PlateCrossheadSubsection,
        PlateCrossheadOrderedListicle,
        PlateCrossheadUnorderedListicle,
        PlateParagraph,
        PlateImage,
        PlateTwitter,
        PlateFacebook,
        PlateInstagram,
        PlateBluesky,
        PlateThreads,
        PlateFooter,
        PlateInfobox,
        PlateEmbeddedContent,
        PlateFrontendComponent,
        PlateInterviewSegment,
        PlatePoll,
        PlateRatingPoll,
        PlateQuote,
        PlateSeparator,
        PlateSlideshow,
        PlateVideoCms,
        PlateYoutube,
        PlateUnorderedList,
        PlateOrderedList,
    ],
    { unionFallback: true },
);

type PlateSnippetElement = z.infer<typeof PlateSnippetElement>;

const UnitySnippetElement = z.discriminatedUnion(
    'type',
    [
        UnityCrosshead,
        UnityParagraph,
        UnityImage,
        UnityTwitter,
        UnityFacebook,
        UnityInstagram,
        UnityBluesky,
        UnityThreads,
        UnityFooter,
        UnityInfobox,
        UnityEmbeddedContent,
        UnityFrontendComponent,
        UnityInterviewSegment,
        UnityPoll,
        UnityRatingPoll,
        UnityQuote,
        UnitySeparator,
        UnitySlideshow,
        UnityVideoCms,
        UnityYoutube,
        UnityUnorderedList,
        UnityOrderedList,
    ],
    { unionFallback: true },
);
type UnitySnippetElement = z.infer<typeof UnitySnippetElement>;

export const UnitySnippet = z.object({
    type: z.literal(ELEMENT_TYPES.SNIPPET),
    elementId: UnityElementId.optional(),
    version: Version,
    content: z.array(UnitySnippetElement),
});

export type UnitySnippet = z.infer<typeof UnitySnippet>;

export const UnityEmbeddedSnippet = z.object({
    type: z.literal(ELEMENT_TYPES.EMBEDDED_SNIPPET),
    elementId: UnityElementId.optional(),
    version: Version,
    contentAttributes: z.object({
        id: z.string(),
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullable(),
        credit: z.string().nullable(),
    }),
    templateElement: z.boolean().optional(),
    mimetype: z.literal(MIME_TYPES.EMBEDDED_SNIPPET),
});

export type UnityEmbeddedSnippet = z.infer<typeof UnityEmbeddedSnippet>;

const SnippetElementsCodec = z.discriminatedUnion(
    'type',
    [
        CrossheadCodec,
        ParagraphCodec,
        ImageCodec,
        TwitterCodec,
        FacebookCodec,
        InstagramCodec,
        BlueskyCodec,
        ThreadsCodec,
        FooterCodec,
        InfoboxCodec,
        EmbeddedContentCodec,
        FrontendComponentCodec,
        InterviewSegmentCodec,
        PollCodec,
        RatingPollCodec,
        QuoteCodec,
        SeparatorCodec,
        SlideshowCodec,
        VideoCmsCodec,
        YoutubeCodec,
        UnorderedListCodec,
        OrderedListCodec,
    ],
    { unionFallback: true },
);

export const PlateSnippet = z.object({
    type: z.literal(ELEMENT_TYPES.SNIPPET),
    id: ElementId,
    data: z.object({
        content: z.array(PlateSnippetElement),
    }),
    children: EmptyChildren,
});

export type SnippetElement = z.infer<typeof PlateSnippet>;

export const PlateEmbeddedSnippet = z.object({
    type: z.literal(ELEMENT_TYPES.EMBEDDED_SNIPPET),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        mimetype: z.literal(MIME_TYPES.EMBEDDED_SNIPPET),
        embed: z.object({
            id: z.string(),
            credit: z.string().nullable(),
            caption: z.array(PlateParagraph),
        }),
    }),
    children: EmptyChildren,
});

export type EmbeddedSnippetElement = z.infer<typeof PlateEmbeddedSnippet>;

export const SnippetCodec = z.codec(UnitySnippet, PlateSnippet, {
    decode: (value): SnippetElement => {
        const content = (value.content ?? []).map((el) => SnippetElementsCodec.decode(el));

        return {
            type: ELEMENT_TYPES.SNIPPET,
            id: value.elementId ?? crypto.randomUUID(),
            data: {
                content,
            },
            children: emptyChildren,
        };
    },

    encode: (value): UnitySnippet => ({
        type: ELEMENT_TYPES.SNIPPET,
        elementId: value.id,
        version: CURRENT_VERSION,
        content: value.data.content.map((element) => SnippetElementsCodec.encode(element)),
    }),
});

export const EmbeddedSnippetCodec = z.codec(UnityEmbeddedSnippet, PlateEmbeddedSnippet, {
    decode: (value): EmbeddedSnippetElement => ({
        type: ELEMENT_TYPES.EMBEDDED_SNIPPET,
        id: value.elementId ?? crypto.randomUUID(),
        children: emptyChildren,
        data: {
            ...(value.templateElement && { templateElement: value.templateElement }),
            mimetype: value.mimetype,
            embed: {
                id: value.contentAttributes.id,
                credit: value.contentAttributes.credit ?? null,
                caption: (value.contentAttributes.caption?.richTextFormat || []).map((paragraph) =>
                    ParagraphCodec.decode(paragraph),
                ),
            },
        },
    }),

    encode: (value): UnityEmbeddedSnippet => ({
        type: ELEMENT_TYPES.EMBEDDED_SNIPPET,
        elementId: value.id,
        version: CURRENT_VERSION,
        contentAttributes: {
            id: value.data.embed.id,
            caption: {
                richTextFormat: (value.data.embed.caption || []).map((paragraph) =>
                    ParagraphCodec.encode(paragraph),
                ),
            },
            credit: null,
        },
        templateElement: value.data.templateElement,
        mimetype: value.data.mimetype,
    }),
});
